// services/astro-bot-miniapp/src/screens/PublicProfile.tsx
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CosmosBackdrop } from '../components/brand/CosmosBackdrop.js';
import styles from './PublicProfile.module.css';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://bot.calestea.com';
const BOT_HANDLE = 'CalesteaBot';

interface PublicProfileNotFound {
  found: false;
}
interface PublicProfileFound {
  found: true;
  displayName: string | null;
  avatarUrl: string | null;
  sun: string | null;
  archetype: { label: string } | null;
}
type ProfileData = PublicProfileNotFound | PublicProfileFound;

export function PublicProfile(): JSX.Element {
  const { userId } = useParams<{ userId: string }>();

  const { data, isError } = useQuery<ProfileData>({
    queryKey: ['public-profile', userId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/api/v1/profile/${userId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json() as Promise<ProfileData>;
    },
    enabled: !!userId,
    staleTime: 60_000,
    retry: 1,
  });

  // Open the bot's Main Mini App with a startapp param (RootRedirect routes
  // `compat_<userId>` into the in-app compat flow). The `/app` short-name form
  // bounced users into the bot chat when no Mini App is registered under that
  // short name; the bare `?startapp=` form targets the configured Main Mini App.
  // NOTE: requires the Mini App to be set as the bot's *Main Mini App* in BotFather.
  const ctaHref = `https://t.me/${BOT_HANDLE}?startapp=compat_${userId ?? ''}`;

  return (
    <div className={styles.root}>
      <CosmosBackdrop />

      <div className={styles.content}>
        {(isError || (data && !data.found)) && (
          <div className={styles.notFound}>
            <p>This profile is not available.</p>
            <a href={`https://t.me/${BOT_HANDLE}`} className={styles.notFoundLink}>
              Open Calestea
            </a>
          </div>
        )}

        {data && data.found && (
          <>
            <div className={styles.avatar}>
              {data.avatarUrl ? (
                <img src={data.avatarUrl} alt="" loading="lazy" />
              ) : (
                <span aria-hidden="true">
                  {data.displayName?.trim().charAt(0).toUpperCase() ?? '✶'}
                </span>
              )}
            </div>

            <p className={styles.name}>
              {data.displayName ?? 'A Calestea user'}
            </p>

            {data.sun && (
              <span className={styles.sunChip}>{'☉'} {data.sun}</span>
            )}

            {data.archetype && (
              <p className={styles.archetype}>{data.archetype.label}</p>
            )}

            <a href={ctaHref} className={styles.cta}>
              {'⚭'} Check compat with me
            </a>
          </>
        )}
      </div>
    </div>
  );
}
